/*
 * ISG simple order form: pure logic (no page access), shared by the browser form and the Node tests.
 * Loaded before assets/isg-order.js. Everything here works in whole pence.
 *
 * Two flows (sections/isg-order-form.liquid decides which one a product gets):
 *   clothing - garment price plus a print charge per print, per garment (Print charge product lines)
 *   included - products tagged exactly "customsublimation": personalisation is included in the price, never a print charge
 *   photo    - products tagged exactly "customphotoprint" (photo prints and frames): printing is in the variant price; a
 *              "Personalisation: Frame only" variant needs no photo. Photos needed per item come from the option names.
 */
(function (root) {
  'use strict';

  var LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  var FILE_TYPES = {
    'image/png': { label: 'PNG', test: function (b) { return b[0] === 0x89 && b[1] === 0x50 && b[2] === 0x4e && b[3] === 0x47; } },
    'image/jpeg': { label: 'JPEG', test: function (b) { return b[0] === 0xff && b[1] === 0xd8 && b[2] === 0xff; } },
    'application/pdf': { label: 'PDF', test: function (b) { return b[0] === 0x25 && b[1] === 0x50 && b[2] === 0x44 && b[3] === 0x46; } }
  };

  function money(pence, currency) {
    var v = (Math.round(pence) / 100).toFixed(2);
    return (currency === 'GBP' || !currency ? '£' : currency + ' ') + v;
  }

  function toInt(v) {
    var n = parseInt(v, 10);
    return isNaN(n) || n < 0 ? 0 : n;
  }

  // "1", "5 pack", "Pack of 10" -> 1, 5, 10. Anything without a number counts as 1 item.
  function packSize(value) {
    var m = String(value == null ? '' : value).match(/(\d+)/);
    var n = m ? parseInt(m[1], 10) : 1;
    return n > 0 ? n : 1;
  }

  // What a file really is, from its first bytes (the file name and browser type are not trusted).
  function sniffType(bytes) {
    for (var t in FILE_TYPES) if (FILE_TYPES[t].test(bytes)) return t;
    return null;
  }

  // iPhone HEIC/HEIF photos: "ftyp" at byte 4 with a HEIC brand.
  function isHeic(b) {
    if (!b || b.length < 12 || b[4] !== 0x66 || b[5] !== 0x74 || b[6] !== 0x79 || b[7] !== 0x70) return false;
    var brand = String.fromCharCode(b[8], b[9], b[10], b[11]);
    return /^(heic|heix|hevc|hevx|heim|heis|mif1|msf1)$/.test(brand);
  }

  function checkFile(file, head, maxMb) {
    if (!file || !file.size) return { ok: false, message: 'This file is empty. Please choose another file.' };
    if (file.size > maxMb * 1048576) {
      return { ok: false, message: 'This file is ' + (file.size / 1048576).toFixed(1) + ' MB. The limit is ' + maxMb + ' MB per file. Please use a smaller file, or email it to us after ordering.' };
    }
    var type = sniffType(head || []);
    if (!type && isHeic(head)) return { ok: false, message: 'This is a HEIC photo (the iPhone format), which we can’t print from. Please save it as a JPEG and choose it again.' };
    if (!type) return { ok: false, message: 'We can only accept PNG, JPEG or PDF files. Please save your artwork in one of those formats.' };
    return { ok: true, type: type, label: FILE_TYPES[type].label };
  }

  // "20:5, 50:10, 100:20" -> [{ min: 20, pct: 5 }, ...] (display of the store's existing automatic discounts only)
  function parseTiers(text) {
    var out = [];
    String(text || '').split(/[,;\n]/).forEach(function (part) {
      var m = part.match(/^\s*(\d+)\s*:\s*(\d+(?:\.\d+)?)\s*$/);
      if (m) out.push({ min: parseInt(m[1], 10), pct: parseFloat(m[2]) });
    });
    return out.sort(function (a, b) { return a.min - b.min; });
  }

  function tierFor(count, tiers) {
    var cur = null, next = null;
    (tiers || []).forEach(function (t) { if (count >= t.min) cur = t; else if (!next) next = t; });
    return { current: cur, next: next, need: next ? next.min - count : 0 };
  }

  // Lines in the "same design" sense: every line gets design A. Otherwise the customer's split, per line and design.
  function allocation(lines, designs, same, alloc) {
    var out = {};
    lines.forEach(function (l) {
      out[l.key] = {};
      if (same || designs.length < 2) { out[l.key][designs[0].id] = l.items; return; }
      designs.forEach(function (d) { out[l.key][d.id] = toInt(alloc && alloc[l.key] && alloc[l.key][d.id]); });
    });
    return out;
  }

  // Items using a design (garments, or individual products for the included flow).
  function itemsPerDesign(lines, designs, al) {
    var n = {};
    designs.forEach(function (d) { n[d.id] = 0; });
    lines.forEach(function (l) { designs.forEach(function (d) { n[d.id] += toInt(al[l.key] && al[l.key][d.id]); }); });
    return n;
  }

  /*
   * Totals.
   * lines: [{ key, items, price (pence per cart unit), units (cart quantity), eligible }]
   * clothing: price is per garment and units === items. included: price is per pack and units = packs.
   * designs: [{ id, prints: [{ sizeKey }] }]; sizes: { key: { price } } (clothing only)
   * bulk: { tiers, countsPrints } - the store's automatic bulk discounts as currently set up in Shopify.
   *
   * 21 Oct 2026 (audit ACT-04 / ACT-05), so the estimate matches the basket to the penny:
   *  - Count: the bulk tiers ("Bulk pricing: 20+ / 50+ / 100+ items") are Shopify minimum-quantity discounts, which
   *    count basket units: every garment AND every print-charge unit (owner's decision, 21 Oct 2026), and packs as
   *    one unit each. The count therefore uses units (cart quantity), not items inside packs.
   *  - Rounding: Shopify works a percentage off per unit and rounds each unit's discount to the penny, half to even.
   *    Example from the audit: 10 hoodies at £16.50 + 10 print charges at £6.50, 5% off: 82.5p -> 82p and
   *    32.5p -> 32p per unit, £11.40 off (basket £218.60). The old estimate took 5% of the whole £230 (£11.50,
   *    giving £218.50). Real order #10012021 also shows a half penny rounded down to even (£1.125 -> £1.12).
   *    Check this against a test basket after any discount change in Shopify.
   */
  function roundHalfEven(x) {
    var r = Math.round(x * 1e6) / 1e6; // clear floating-point noise first
    var f = Math.floor(r), d = r - f;
    if (d > 0.5) return f + 1;
    if (d < 0.5) return f;
    return f % 2 === 0 ? f : f + 1;
  }

  // Discount on one unit costing `pence`, at `pct` percent, as Shopify rounds it.
  function unitDiscount(pence, pct) {
    return roundHalfEven(pence * pct / 100);
  }

  function totals(flow, lines, designs, al, sizes, bulk) {
    var goods = 0, prints = 0, printUnits = 0, items = 0, eligibleUnits = 0, eligibleValue = 0;
    var countsPrints = !!(bulk && bulk.countsPrints);
    var printGroups = []; // [{ price, units }] one per print and design, as the basket's print-charge units
    lines.forEach(function (l) {
      goods += l.price * l.units;
      items += l.items;
      if (l.eligible) { eligibleUnits += l.units; eligibleValue += l.price * l.units; }
    });
    if (flow === 'clothing') {
      var per = itemsPerDesign(lines, designs, al);
      designs.forEach(function (d) {
        d.prints.forEach(function (p) {
          var s = sizes[p.sizeKey];
          if (!s) return;
          prints += s.price * per[d.id];
          printUnits += per[d.id];
          printGroups.push({ price: s.price, units: per[d.id] });
        });
      });
    }
    var anyEligible = lines.some(function (l) { return l.eligible; });
    var count = 0, base = 0;
    if (anyEligible) {
      count = eligibleUnits + (countsPrints ? printUnits : 0);
      base = eligibleValue + (countsPrints ? prints : 0);
    }
    var t = tierFor(count, anyEligible && bulk ? bulk.tiers : []);
    var discount = 0;
    if (t.current) {
      var pct = t.current.pct;
      lines.forEach(function (l) { if (l.eligible) discount += unitDiscount(l.price, pct) * l.units; });
      if (countsPrints) printGroups.forEach(function (g) { discount += unitDiscount(g.price, pct) * g.units; });
    }
    return {
      items: items, goods: goods, prints: prints, printUnits: printUnits, subtotal: goods + prints,
      bulkCount: count, bulk: t, discount: discount, total: goods + prints - discount
    };
  }

  function nonEmptyLines(text) {
    return String(text || '').split(/\r?\n/).map(function (s) { return s.trim(); }).filter(Boolean);
  }

  /*
   * Everything that stops the order going into the basket, in plain words. state:
   * { flow, lines, designs, same, alloc, files: { id: { status } }, settings: { requirePrint, allowTextOnly, layouts } }
   * Clothing design: { id, prints: [{ location, sizeKey, fileIds, text, textEach, perItem, notes }] }
   * Included design: { id, fileIds, text, textEach, perItem, layout, notes }
   * perItem: different text on each item; textEach: { lineKey: "one per line" }.
   */
  function problems(state) {
    var out = [];
    var lines = state.lines, designs = state.designs, s = state.settings || {};
    var many = designs.length > 1 && !state.same;
    if (!lines.length || !lines.some(function (l) { return l.items > 0; })) {
      out.push(state.flow === 'clothing' ? 'Choose a colour and enter how many of each size you want.' : 'Choose what you’d like and how many.');
    }
    lines.forEach(function (l) {
      // stock is in basket units (garments, or packs for pack-priced products)
      if (l.stock != null && l.units > l.stock) out.push('Only ' + l.stock + ' of ' + l.label + ' available.');
    });
    if (state.flow === 'photo') return photoProblems(state, out);
    var al = allocation(lines, designs, state.same, state.alloc);
    if (many) {
      lines.forEach(function (l) {
        var sum = 0;
        designs.forEach(function (d) { sum += toInt(al[l.key][d.id]); });
        if (sum !== l.items) out.push(l.label + ': ' + (sum < l.items ? (l.items - sum) + ' still need a design' : (sum - l.items) + ' too many assigned') + ' (' + l.items + ' ordered).');
      });
      var per = itemsPerDesign(lines, designs, al);
      designs.forEach(function (d) { if (!per[d.id]) out.push('Design ' + d.id + ' isn’t used on any item. Assign it to some items or remove it.'); });
    }
    function fileState(ids, where) {
      (ids || []).forEach(function (id) {
        var f = state.files[id];
        if (!f) out.push(where + ': a file is missing. Please add it again.');
        else if (f.status === 'checking') out.push(where + ': a file is still being checked.');
        else if (f.status === 'error') out.push(where + ': remove or replace the file that couldn’t be used.');
      });
    }
    function perItemText(d, where, textOf) {
      lines.forEach(function (l) {
        var n = toInt(al[l.key][d.id]);
        if (!n) return;
        var got = nonEmptyLines(textOf[l.key]).length;
        if (got !== n) out.push(where + ': enter ' + n + ' line' + (n === 1 ? '' : 's') + ' of text for ' + l.label + ' (one per item; ' + got + ' entered).');
      });
    }
    designs.forEach(function (d) {
      var dl = many ? 'Design ' + d.id + ', ' : '';
      if (state.flow === 'clothing') {
        if (s.requirePrint && !d.prints.length) out.push((many ? 'Design ' + d.id + ': ' : '') + 'add at least one print location.');
        d.prints.forEach(function (p, i) {
          var where = dl + 'print ' + (i + 1);
          if (!p.location) out.push(where + ': choose where it goes.');
          else if (!p.sizeKey) out.push(where + ': choose a print size.');
          else if (p.allowed && p.allowed.indexOf(p.sizeKey) === -1) out.push(where + ': that print size isn’t available at this location.');
          var hasText = p.perItem || !!String(p.text || '').trim();
          if (!(p.fileIds && p.fileIds.length) && !hasText) out.push(where + ': add your artwork or the text to print.');
          fileState(p.fileIds, where);
          if (p.perItem) perItemText(d, where, p.textEach || {});
        });
      } else {
        var where = many ? 'Design ' + d.id : 'Your design';
        var hasText = d.perItem || !!String(d.text || '').trim();
        var hasFiles = !!(d.fileIds && d.fileIds.length);
        if (!hasFiles && !(hasText && s.allowTextOnly)) out.push(where + ': ' + (s.allowTextOnly ? 'add a photo or artwork, or the text you’d like.' : 'add a photo or artwork.'));
        if (s.layouts && s.layouts.length && !d.layout) out.push(where + ': choose a layout.');
        fileState(d.fileIds, where);
        if (d.perItem) perItemText(d, where, d.textEach || {});
      }
    });
    return out;
  }

  /*
   * Print sizes a print area can use ("only sizes that fit", ISG 19 Oct 2026). sizes: [{ key, maxW, maxH }] in cm.
   * The smallest size is always offered (artwork is scaled down to fit the area). A bigger size is offered only when
   * the area can hold a print that is bigger than the next size down, i.e. one that would actually be charged at that
   * size. No area (0 or missing) = every size. Returns the keys, smallest first.
   */
  function sizesThatFit(sizes, areaW, areaH) {
    var list = (sizes || []).slice().sort(function (a, b) { return a.maxW * a.maxH - b.maxW * b.maxH; });
    var w = parseFloat(areaW) || 0, h = parseFloat(areaH) || 0;
    if (!(w > 0 && h > 0)) return list.map(function (s) { return s.key; });
    return list.filter(function (s, i) {
      if (i === 0) return true;
      var prev = list[i - 1];
      return Math.min(w, s.maxW) > prev.maxW || Math.min(h, s.maxH) > prev.maxH;
    }).map(function (s) { return s.key; });
  }

  // "30 x 15", "30x15 cm", "30 × 15" -> [30, 15]; anything else -> null.
  function parseArea(text) {
    var m = String(text == null ? '' : text).match(/(\d+(?:\.\d+)?)\s*(?:cm)?\s*[x×]\s*(\d+(?:\.\d+)?)/i);
    return m ? [parseFloat(m[1]), parseFloat(m[2])] : null;
  }

  /* ---------- photo prints and frames ---------- */
  var FIT = {
    fill: 'Fill the print area (edges trimmed to fit)',
    whole: 'Whole photo (white border where the shapes differ)'
  };
  var A_SIZES = { A3: [11.69, 16.54], A4: [8.27, 11.69], A5: [5.83, 8.27], A6: [4.13, 5.83] };

  // The print size in inches named in an option value or title: "5 x 7 inch", "8 x 10 inch (11 x 14 inch frame)" (the
  // photo, not the frame), "A4". null when none is named (for example "9 photos").
  function printSizeIn(values, title) {
    var list = (values || []).map(String).concat([String(title || '')]);
    for (var i = 0; i < list.length; i++) {
      var v = list[i].replace(/\([^)]*\bframes?\b[^)]*\)/gi, ' ');
      var m = v.match(/(\d+(?:\.\d+)?)\s*x\s*(\d+(?:\.\d+)?)\s*(?:inch|in\b|")/i);
      if (m) return [parseFloat(m[1]), parseFloat(m[2])];
      var a = v.match(/\bA([3-6])\b/);
      if (a) return A_SIZES['A' + a[1]].slice();
    }
    return null;
  }

  /*
   * What one variant needs. options: the variant's option values; persIndex: the "Personalisation" option (or -1);
   * packIndex: the "Quantity" pack option (or -1); override: a product's own photo count (product field), if set.
   * Photos per item: "N photos" -> N, "double" -> 2, "triple" -> 3 in an option value; otherwise "N-Photo" in the title;
   * otherwise 1. "Frame only" -> no photos.
   */
  function photoInfo(options, persIndex, packIndex, title, override) {
    options = options || [];
    var printed = !(persIndex >= 0 && /frame\s*only/i.test(String(options[persIndex] || '')));
    var vals = options.filter(function (o, i) { return i !== persIndex && i !== packIndex; }).map(String);
    var count = toInt(override);
    var source = count ? 'product field' : '';
    if (!count) {
      for (var i = 0; i < vals.length && !count; i++) {
        var m = vals[i].match(/(\d+)\s*photos?\b/i);
        if (m) count = parseInt(m[1], 10);
        else if (/\bdouble\b/i.test(vals[i])) count = 2;
        else if (/\btriple\b/i.test(vals[i])) count = 3;
      }
      if (count) source = 'option';
    }
    if (!count) {
      var t = String(title || '').match(/(\d+)[\s-]*photos?\b/i);
      if (t) { count = parseInt(t[1], 10); source = 'title'; }
    }
    if (!count) { count = 1; source = 'default'; }
    return { printed: printed, count: printed ? count : 0, perItem: count, source: source, sizeIn: printSizeIn(vals, title) };
  }

  // Pixels per inch a photo of px [w, h] gets at sizeIn [w, h] inches (orientation matched: long side to long side).
  function photoPpi(px, sizeIn, fit) {
    if (!px || !sizeIn || !px[0] || !px[1]) return null;
    var s = Math.min(px[0], px[1]), l = Math.max(px[0], px[1]);
    var S = Math.min(sizeIn[0], sizeIn[1]), L = Math.max(sizeIn[0], sizeIn[1]);
    var a = s / S, b = l / L;
    return Math.round(fit === 'whole' ? Math.max(a, b) : Math.min(a, b));
  }

  function sizeName(sizeIn) {
    for (var k in A_SIZES) if (A_SIZES[k][0] === sizeIn[0] && A_SIZES[k][1] === sizeIn[1]) return k;
    return sizeIn[0] + ' × ' + sizeIn[1] + ' inch';
  }

  // A warning (never a block) when a photo has too few pixels for its print size.
  function qualityNote(px, sizeIn, fit, minPpi) {
    var ppi = photoPpi(px, sizeIn, fit);
    if (ppi == null || ppi >= (minPpi || 150)) return null;
    return { ppi: ppi, message: 'This photo is ' + px[0] + ' × ' + px[1] + ' pixels: about ' + ppi + ' pixels per inch at ' + sizeName(sizeIn) +
      ', so it may print soft or blurry. If you have a larger copy (the original from your camera or phone, not a screenshot or one sent by message), use that instead. You can still order with this one.' };
  }

  function photoProblems(state, out) {
    var max = (state.settings && state.settings.maxPhotoSets) || 20;
    (state.photoRows || []).forEach(function (r) {
      if (!r.printed || !r.units) return;
      if (!r.same && r.units > max) out.push(r.label + ': for more than ' + max + ' ' + r.noun + 's with different photos, choose the same photos for all of them, or add them as separate choices.');
      r.sets.forEach(function (set, si) {
        var where = r.label + (r.sets.length > 1 ? ', ' + r.noun + ' ' + (si + 1) : '');
        var missing = set.slots.filter(function (x) { return !x; }).length;
        if (missing) out.push(where + ': ' + (r.count === 1 ? 'add your photo.' : 'add ' + missing + ' more photo' + (missing === 1 ? '' : 's') + ' (' + r.count + ' needed).'));
        set.slots.forEach(function (id) {
          if (!id) return;
          var f = state.files[id];
          if (!f) out.push(where + ': a photo is missing. Please add it again.');
          else if (f.status === 'checking') out.push(where + ': a photo is still being checked.');
          else if (f.status === 'error') out.push(where + ': remove or replace the photo that couldn’t be used.');
        });
      });
    });
    return out;
  }

  function photoPlan(state, ref) {
    var out = { lines: [], charges: [] };
    (state.photoRows || []).forEach(function (r) {
      if (!r.units) return;
      if (!r.printed) {
        out.lines.push({ id: r.variantId, quantity: r.units, properties: { 'Design ref': ref }, files: [], label: r.label });
        return;
      }
      var sets = r.same ? r.sets.slice(0, 1) : r.sets;
      sets.forEach(function (set, si) {
        var props = { 'Design ref': ref }, files = [];
        if (sets.length > 1) {
          props[r.noun.charAt(0).toUpperCase() + r.noun.slice(1)] = (si + 1) + ' of ' + sets.length;
          props._isg_set = (si + 1) + '/' + sets.length;
        }
        props['Photo printing'] = 'Included in the price';
        set.slots.forEach(function (id, i) {
          var prop = r.count === 1 ? 'Photo' : 'Photo ' + (i + 1);
          props[prop] = '';
          files.push({ prop: prop, id: id });
        });
        props.Fit = FIT[set.fit] || FIT.fill;
        if (String(set.notes || '').trim()) props[r.count > 1 ? 'Photo positions and notes' : 'Instructions'] = String(set.notes).trim();
        out.lines.push({ id: r.variantId, quantity: sets.length > 1 ? 1 : r.units, properties: props, files: files,
          label: r.label + (sets.length > 1 ? ', ' + r.noun + ' ' + (si + 1) : '') });
      });
    });
    return out;
  }

  function makeRef(rand) {
    var chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789', out = '';
    for (var i = 0; i < 8; i++) out += chars[(rand ? rand() : Math.floor(Math.random() * 256)) % chars.length];
    return 'ISG-' + out;
  }

  /*
   * The basket lines to add, in order. Files are named by id (fileRef: { prop, id }); the browser attaches the actual
   * file on its first use and the stored Shopify link after that, so each file is uploaded exactly once.
   * Returns { lines: [{ id, quantity, properties: {}, files: [{ prop, id }] }], charges: [{ id, quantity, properties }] }.
   */
  function basketPlan(state, ref) {
    if (state.flow === 'photo') return photoPlan(state, ref);
    var lines = state.lines, designs = state.designs, sizes = state.sizes || {};
    var many = designs.length > 1 && !state.same;
    var al = allocation(lines, designs, state.same, state.alloc);
    var out = { lines: [], charges: [] };
    function fileProps(props, files, base, ids) {
      (ids || []).forEach(function (id, i) {
        var prop = base + (i ? ' ' + (i + 1) : '');
        props[prop] = '';
        files.push({ prop: prop, id: id });
      });
    }
    if (state.flow === 'clothing') {
      var chargeQty = {}; // designId -> chargeVariantId -> qty
      lines.forEach(function (l) {
        designs.forEach(function (d) {
          var q = toInt(al[l.key][d.id]);
          if (!q) return;
          var props = {}, files = [], counts = {};
          props['Design ref'] = ref + '-' + d.id;
          if (many) props.Design = 'Design ' + d.id;
          d.prints.forEach(function (p, i) {
            var n = 'Print ' + (i + 1), s = sizes[p.sizeKey] || {};
            props[n] = p.locationLabel + ' – ' + s.title + ' – ' + money(s.price, state.currency) + ' per item';
            fileProps(props, files, n + ' artwork', p.fileIds);
            if (p.perItem) props[n + ' text, one per item'] = nonEmptyLines((p.textEach || {})[l.key]).join(' | ');
            else if (String(p.text || '').trim()) props[n + ' text'] = String(p.text).trim();
            if (String(p.notes || '').trim()) props[n + ' instructions'] = String(p.notes).trim();
            if (s.variantId) counts[s.variantId] = (counts[s.variantId] || 0) + 1;
          });
          props._isg_prints = JSON.stringify(counts);
          out.lines.push({ id: l.variantId, quantity: q, properties: props, files: files, label: l.label, designId: d.id });
          chargeQty[d.id] = chargeQty[d.id] || {};
          Object.keys(counts).forEach(function (vid) { chargeQty[d.id][vid] = (chargeQty[d.id][vid] || 0) + counts[vid] * q; });
        });
      });
      designs.forEach(function (d) {
        Object.keys(chargeQty[d.id] || {}).forEach(function (vid) {
          out.charges.push({ id: parseInt(vid, 10), quantity: chargeQty[d.id][vid],
            properties: { For: state.productTitle + (many ? ' – Design ' + d.id : ''), 'Design ref': ref + '-' + d.id, _isg_charge: '1' } });
        });
      });
    } else {
      lines.forEach(function (l) {
        var props = {}, files = [];
        props['Design ref'] = ref;
        props.Personalisation = 'Included in the price';
        var used = designs.filter(function (d) { return toInt(al[l.key][d.id]) > 0; });
        if (!used.length) return;
        used.forEach(function (d) {
          var pre = many ? 'Design ' + d.id + ' ' : '';
          var n = toInt(al[l.key][d.id]);
          if (many) props['Design ' + d.id] = n + ' of ' + l.items + ' item' + (l.items === 1 ? '' : 's');
          if (d.layout) props[pre + (pre ? 'layout' : 'Layout')] = d.layout;
          fileProps(props, files, pre + (pre ? 'photo or artwork' : 'Photo or artwork'), d.fileIds);
          if (d.perItem) props[pre + (pre ? 'text, one per item' : 'Text, one per item')] = nonEmptyLines((d.textEach || {})[l.key]).join(' | ');
          else if (String(d.text || '').trim()) props[pre + (pre ? 'text' : 'Text')] = String(d.text).trim();
          if (String(d.notes || '').trim()) props[pre + (pre ? 'instructions' : 'Instructions')] = String(d.notes).trim();
        });
        out.lines.push({ id: l.variantId, quantity: l.units, properties: props, files: files, label: l.label });
      });
    }
    return out;
  }

  // Basket lines that belong to an attempt (its "Design ref" is the base or base-<design>).
  function linesOfRef(cart, base) {
    return ((cart && cart.items) || []).filter(function (it) {
      var r = it.properties && it.properties['Design ref'];
      return r && (r === base || r.indexOf(base + '-') === 0);
    });
  }

  /*
   * Is this attempt fully and correctly in the basket? Every planned line with its quantity, every file stored as a
   * link, and the print charges matching. Returns a list of what's wrong (empty = correct).
   */
  function verifyAttempt(cart, plan, base) {
    var wrong = [];
    var mine = linesOfRef(cart, base);
    var pool = mine.slice();
    plan.lines.forEach(function (pl) {
      var idx = -1;
      for (var i = 0; i < pool.length; i++) {
        var it = pool[i];
        if (it.variant_id !== pl.id || (it.properties || {})['Design ref'] !== pl.properties['Design ref']) continue;
        // Exact match on the keys that link lines together; the rest only need to be there (Shopify may tidy text).
        var same = Object.keys(pl.properties).every(function (k) {
          var v = (it.properties || {})[k];
          if (k === 'Design' || k === '_isg_prints' || k === '_isg_set') return String(v) === String(pl.properties[k]);
          return v != null && String(v) !== '';
        });
        if (same) { idx = i; break; }
      }
      if (idx < 0) { wrong.push('missing: ' + pl.label); return; }
      var it2 = pool.splice(idx, 1)[0];
      if (it2.quantity !== pl.quantity) wrong.push('quantity: ' + pl.label + ' is ' + it2.quantity + ', expected ' + pl.quantity);
      pl.files.forEach(function (f) {
        var v = (it2.properties || {})[f.prop];
        if (!isStoredLink(v)) wrong.push('file not stored: ' + pl.label + ' / ' + f.prop);
      });
    });
    plan.charges.forEach(function (c) {
      var got = pool.filter(function (it) { return it.variant_id === c.id && (it.properties || {})['Design ref'] === c.properties['Design ref'] && (it.properties || {})._isg_charge; });
      var q = got.reduce(function (a, it) { return a + it.quantity; }, 0);
      if (q !== c.quantity) wrong.push('print charge ' + c.properties['Design ref'] + ': ' + q + ', expected ' + c.quantity);
      got.forEach(function (it) { pool.splice(pool.indexOf(it), 1); });
    });
    if (pool.length) wrong.push(pool.length + ' unexpected line(s)');
    return wrong;
  }

  // A file that Shopify has stored arrives back as a link (never a browser "blob:" address).
  function isStoredLink(v) {
    return typeof v === 'string' && /^(https?:)?\/\//.test(v);
  }

  var api = {
    LETTERS: LETTERS, FILE_TYPES: FILE_TYPES, money: money, toInt: toInt, packSize: packSize, sniffType: sniffType,
    checkFile: checkFile, parseTiers: parseTiers, tierFor: tierFor, allocation: allocation, itemsPerDesign: itemsPerDesign,
    totals: totals, roundHalfEven: roundHalfEven, unitDiscount: unitDiscount, problems: problems, nonEmptyLines: nonEmptyLines, makeRef: makeRef, basketPlan: basketPlan,
    linesOfRef: linesOfRef, verifyAttempt: verifyAttempt, isStoredLink: isStoredLink,
    sizesThatFit: sizesThatFit, parseArea: parseArea,
    FIT: FIT, isHeic: isHeic, printSizeIn: printSizeIn, photoInfo: photoInfo, photoPpi: photoPpi, qualityNote: qualityNote
  };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  else root.ISGOrderCore = api;
})(typeof window !== 'undefined' ? window : globalThis);
